import { AxiosError } from 'axios';
import useHandleAppException, {
  AppExceptionResponseData,
} from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import { useOAuthConsentQuery } from '../../../services/modules/oauth/oauth.queries';
import { OAuthConsentLocationState } from '../pages/OAuthConsentPage';

export interface OAuthConsentDetails {
  oauthRequestId: string;
  clientName: string;
  scopes: string[];
}

interface UseOAuthConsentDetailsReturn {
  details: OAuthConsentDetails | null;
  isLoading: boolean;
  errorMessage: string | null;
}

const readConsentExceptionData = (error: unknown): AppExceptionResponseData | null => {
  const exceptionData = (error as AxiosError<AppExceptionResponseData>)?.response?.data;
  return exceptionData?.code ? exceptionData : null;
};

const useOAuthConsentDetails = (
  state: OAuthConsentLocationState | undefined,
  oauthRequestId: string,
): UseOAuthConsentDetailsReturn => {
  const { handleError } = useHandleAppException();
  const isFetchEnabled = !state && !!oauthRequestId;
  const query = useOAuthConsentQuery({ oauthRequestId, enabled: isFetchEnabled });

  if (state) {
    return {
      details: {
        oauthRequestId: state.oauthRequestId,
        clientName: state.clientName,
        scopes: state.scopes,
      },
      isLoading: false,
      errorMessage: null,
    };
  }

  if (query.isError) {
    const exceptionData = readConsentExceptionData(query.error);
    const errorMessage = handleError(
      exceptionData ?? { code: LangEnum.SNACKBAR_ERROR_OCCURRED_TRY_AGAIN, message: '' },
    );
    return { details: null, isLoading: false, errorMessage };
  }

  if (!query.data) {
    return { details: null, isLoading: isFetchEnabled, errorMessage: null };
  }

  return {
    details: {
      oauthRequestId,
      clientName: query.data.client_name,
      scopes: query.data.scopes,
    },
    isLoading: false,
    errorMessage: null,
  };
};

export default useOAuthConsentDetails;
